import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaArrowRight, FaSearch, FaTags, FaShare } from 'react-icons/fa';
import './blog.css';

function Blog() {
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Sample blog data
  const blogPosts = [
{
  id: 1,
  title: "The Future of AI in Business Transformation",
  excerpt: "Discover how artificial intelligence is revolutionizing business processes and creating new opportunities for growth.",
  date: "June 15, 2023",
  author: "Sarah Johnson",
  category: "ai",
  image: "https://images.unsplash.com/photo-1593642532973-d31b6557fa68?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
  readTime: "5 min read"
},
    {
      id: 2,
      title: "Cloud Migration Best Practices for Enterprises",
      excerpt: "Learn the essential steps for a successful cloud migration strategy that minimizes downtime and maximizes efficiency.",
      date: "May 28, 2023",
      author: "Michael Chen",
      category: "cloud",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80",
      readTime: "7 min read"
    },
    {
      id: 3,
      title: "Cybersecurity Trends to Watch in 2023",
      excerpt: "Stay ahead of threats with our analysis of the most important cybersecurity developments this year.",
      date: "April 12, 2023",
      author: "Priya Sharma",
      category: "security",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80",
      readTime: "6 min read"
    },
    {
      id: 4,
      title: "Digital Transformation in Post-Pandemic Era",
      excerpt: "How businesses are adapting their digital strategies to thrive in the new normal.",
      date: "March 30, 2023",
      author: "David Wilson",
      category: "digital",
      image: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80",
      readTime: "8 min read"
    },
    {
      id: 5,
      title: "Machine Learning for Predictive Analytics",
      excerpt: "Leverage machine learning algorithms to predict market trends and customer behavior with greater accuracy.",
      date: "February 18, 2023",
      author: "Alex Rodriguez",
      category: "ai",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80",
      readTime: "9 min read"
    },
    {
      id: 6,
      title: "Hybrid Cloud Solutions: Benefits and Challenges",
      excerpt: "Exploring the advantages and potential pitfalls of hybrid cloud infrastructure for modern businesses.",
      date: "January 5, 2023",
      author: "Emily Watson",
      category: "cloud",
      image: "https://images.unsplash.com/photo-1451188502541-13943edb6acb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80",
      readTime: "7 min read"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Topics' },
    { id: 'ai', name: 'AI & Machine Learning' },
    { id: 'cloud', name: 'Cloud Solutions' },
    { id: 'security', name: 'Cybersecurity' },
    { id: 'digital', name: 'Digital Transformation' }
  ];

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="glaxmore-blog">
      {/* Blog Header */}
      <section className="blog-header">
        <div className="blog-header-content">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            GLAXMORE <span className="accent-text">Insights</span>
          </motion.h1>
          <motion.p 
            className="blog-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Expert perspectives on technology, innovation, and digital transformation
          </motion.p>
        </div>
      </section>

      {/* Blog Content */}
      <div className="blog-container">
        <div className="blog-main">
          {/* Category Filters */}
          <div className="blog-filters">
            {categories.map(category => (
              <button
                key={category.id}
                className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="blog-grid">
            {filteredPosts.map(post => (
              <motion.article 
                key={post.id} 
                className="blog-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <div className="blog-card-image">
                  <img src={post.image} alt={post.title} />
                  <div className="blog-card-category">{post.category.toUpperCase()}</div>
                </div>
                <div className="blog-card-content">
                  <h2>{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-meta">
                    <span><FaCalendarAlt /> {post.date}</span>
                    <span><FaUser /> {post.author}</span>
                  </div>
                  <div className="blog-card-footer">
                    <span className="read-time">{post.readTime}</span>
                    <button className="read-more-btn">
                      Read More <FaArrowRight />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        {/* Blog Sidebar */}
        <aside className="blog-sidebar">
          {/* Search Widget */}
          <div className="sidebar-widget">
            <h3>Search Articles</h3>
            <div className="search-box">
              <input type="text" placeholder="Search keywords..." />
              <button><FaSearch /></button>
            </div>
          </div>

          {/* Popular Posts Widget */}
          <div className="sidebar-widget">
            <h3>Popular Topics</h3>
            <div className="popular-topics">
              {['AI Innovation', 'Cloud Security', 'Digital Transformation', 'IoT Solutions', 'Blockchain'].map(topic => (
                <a key={topic} href="#!" className="topic-tag">
                  <FaTags /> {topic}
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter Widget */}
          <div className="sidebar-widget newsletter-widget">
            <h3>Stay Updated</h3>
            <p>Subscribe to our newsletter for the latest insights</p>
            <div className="newsletter-form">
              <input type="email" placeholder="Your email address" />
              <button>Subscribe</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Blog;